import { createFileRoute, Link } from "@tanstack/react-router"
import { useBreakpoints } from "@/utils/use-breakpoints"
import { Page } from "@components/Page"
import {
  Button,
  Stack,
  Group,
  Select,
  InputWrapper,
  Checkbox,
} from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { useTranslation } from "react-i18next"
import {
  ResponsiveFilterGroup,
  useResponsiveFilterQueryOptions,
} from "../../-components/hideable-filter-group"
import { SearchTextInput } from "../../-components/search-text-input"
import { useLocalizedUserTypeFilter } from "./-components/use-localized-user-type-filter"
import { InfiniteScrollingTable } from "@/routes/-components/custom-tanstack-table"
import { useDefaultInfiniteQuery } from "@/utils/useDefaultInfiniteQuery"
import {
  getPaginatedUserData,
  UserDto,
  UserTypeFilter,
  useGetProviderConfigs,
} from "@/orval/gen"
import { useUserTableColumns } from "./-components/use-user-table-columns"
import { BatchLinkExternalLoginsModal } from "./-components/batch-link-external-logins-modal"

export interface Search {
  searchString?: string
  userTypeFilter?: UserTypeFilter
  filterInactive?: boolean
}

export const Route = createFileRoute("/app/admin-settings/users/")({
  component: UsersPage,
  validateSearch: (search: Search) =>
    ({
      ...search,
      userTypeFilter: search.userTypeFilter ?? UserTypeFilter.UsersAndGroups,
    }) as Search,
})

function UsersPage() {
  const search = Route.useSearch()
  const { t } = useTranslation("app_server")
  const navigate = Route.useNavigate()
  const getLocalizedUserTypeFilter = useLocalizedUserTypeFilter()

  const [filterOpened, setFilterOpened] = React.useState(false)
  const queryOptions = useResponsiveFilterQueryOptions(filterOpened)
  const toggleFilter = () => setFilterOpened((prev) => !prev)
  const [batchLinkOpened, batchLinkModal] = useDisclosure()
  const { xs } = useBreakpoints()
  const { data: ssoProviders = [] } = useGetProviderConfigs()
  const hasSsoProviders = ssoProviders.length > 0

  const queryResult = useDefaultInfiniteQuery(
    getPaginatedUserData,
    (pageRequest) => ({
      ...search,
      ...pageRequest,
      userTypeFilter: search.userTypeFilter ?? UserTypeFilter.UsersAndGroups,
    }),
    queryOptions
  )
  const { columns } = useUserTableColumns()
  const setSearch = (
    input: Partial<Search> | ((prev: Search) => Partial<Search>)
  ) => {
    const newSearch = typeof input === "function" ? input(search) : input
    navigate({ search: { ...search, ...newSearch } })
  }

  return (
    <Page
      buttonGroupProps={{
        children: (
          <>
            <ResponsiveFilterGroup.Button
              filters={{
                includeInactive: !search.filterInactive,
                userType:
                  search.userTypeFilter !== UserTypeFilter.UsersAndGroups,
              }}
              visible={filterOpened}
              onChange={toggleFilter}
            />
            {xs ? null : (
              <>
                <Button component={Link} to="new">
                  {t("CreateUser")}
                </Button>
              </>
            )}
          </>
        ),
      }}
      dottedMenuProps={{
        items: [
          xs
            ? {
                children: t("CreateUser"),
                onClick: () =>
                  navigate({ to: "/app/admin-settings/users/new" }),
              }
            : null,
          hasSsoProviders
            ? {
                children: t("SsoLinkUsers"),
                onClick: batchLinkModal.open,
              }
            : null,
        ],
      }}
    >
      <Stack h={"100%"}>
        <Group>
          <SearchTextInput
            onChange={(e) =>
              setSearch((prev) => ({ ...prev, searchString: e.target.value }))
            }
            value={search.searchString}
          />
          <ResponsiveFilterGroup
            align="flex-start"
            visible={filterOpened}
            setVisible={setFilterOpened}
            filters={{
              includeInactive: !search.filterInactive,
              userType: search.userTypeFilter !== UserTypeFilter.UsersAndGroups,
            }}
            onReset={() =>
              setSearch({
                filterInactive: true,
                userTypeFilter: UserTypeFilter.UsersAndGroups,
              })
            }
          >
            <Select
              label={t("UserType")}
              data={Object.values(UserTypeFilter).map((v) => ({
                label: getLocalizedUserTypeFilter(v),
                value: v,
              }))}
              value={search.userTypeFilter}
              onChange={(value) =>
                setSearch((prev) => ({
                  ...prev,
                  userTypeFilter: value as UserTypeFilter,
                }))
              }
            />
            <InputWrapper label={t("ShowInactive")}>
              <Checkbox
                checked={!search.filterInactive}
                onChange={(event) =>
                  setSearch((prev) => ({
                    ...prev,
                    filterInactive: !event.currentTarget.checked,
                  }))
                }
              />
            </InputWrapper>
          </ResponsiveFilterGroup>
        </Group>

        <InfiniteScrollingTable<UserDto>
          id="users-table"
          infiniteQueryResult={queryResult}
          columnDefinitions={columns}
          getRowProps={(row) => ({
            onClick: () =>
              navigate({
                to: "/app/admin-settings/users/$id",
                params: { id: row.original.userId.toString() },
              }),
          })}
        />
      </Stack>
      {hasSsoProviders && batchLinkOpened && (
        <BatchLinkExternalLoginsModal opened onClose={batchLinkModal.close} />
      )}
    </Page>
  )
}
